import { Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicModalOperationResult, DialogModalComponent, IBasicModalComponentInstance } from 'src/app/shared/components/dialog-modal/dialog-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { DismissReason } from 'src/app/shared/extensions/ng-active-modal/dismiss-reason';
import { Invite } from 'src/app/core/models/invite-model';
import { UserRoles } from 'src/app/core/models/enums/userRoles';
import { UserStatus } from 'src/app/core/models/enums/userStatus';
import { CredentialRevocationService } from 'src/app/core/services/api/credential-revocation.service';
import { RevocationReason } from 'src/app/core/models/enums/revocationReason';
import { BlockUnblockService } from 'src/app/core/services/api/blockunblock.service';
import { BlockType } from 'src/app/core/models/enums/blockType';
import { OutgoingChatMessageStatusEnum } from 'src/app/core/models/enums/IncomingChatMessageStatusEnum';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss']
})
export class EditUserComponent implements OnInit {
  showSpinner: boolean = false;
  userInvite: Invite;
  userRoles = UserRoles;
  userStatus = UserStatus;
  revocationReason = RevocationReason;
  blockUnblockType = BlockType;

  showRevokeForm: boolean = false;
  selectedReason: RevocationReason = RevocationReason.BajaAdministrativa;
  reasonText: string = '';

  revocationReasonLabels = [
    { value: RevocationReason.BajaAdministrativa, label: 'Baja administrativa' },
    { value: RevocationReason.ErrorAlta,          label: 'Error en el alta' },
    { value: RevocationReason.PerdidaWallet,      label: 'Pérdida o compromiso de la Wallet' },
    { value: RevocationReason.Otro,               label: 'Otro' },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    public translate: TranslateService,
    private credentialRevocationService: CredentialRevocationService,
    private blockUnblockService: BlockUnblockService,
  ) { }

  ngOnInit() { }

  get isRegistered(): boolean {
    return this.userInvite?.currentStatus === UserStatus.Registered;
  }

  get isRevoked(): boolean {
    return this.userInvite?.currentStatus === UserStatus.Revoked;
  }

  get isBlocked(): boolean {
    return this.userInvite?.currentStatus === UserStatus.Blocked;
  }

  get canBlockUnblock(): boolean {
    return this.isRegistered || this.isBlocked;
  }

  get canConfirmRevoke(): boolean {
    if (this.selectedReason !== RevocationReason.Otro) return true;
    return !!this.reasonText?.trim();
  }

  get initials(): string {
    const parts = (this.userInvite?.fullName ?? '').trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  }

  get statusBadgeClass(): string {
    switch (this.userInvite?.currentStatus) {
      case UserStatus.Registered: return 'badge-success';
      case UserStatus.Blocked:    return 'badge-danger';
      case UserStatus.Revoked:    return 'badge-secondary';
      default:                    return 'badge-light';
    }
  }

  toggleCredential() {
    if (this.isRegistered) {
      this.showRevokeForm = true;
    } else if (this.isRevoked) {
      this.confirmReactivate();
    }
  }

  cancelRevoke() {
    this.showRevokeForm = false;
    this.selectedReason = RevocationReason.BajaAdministrativa;
    this.reasonText = '';
  }

  async confirmRevoke() {
    if (!this.canConfirmRevoke) return;
    try {
      this.showSpinner = true;
      await this.credentialRevocationService.revoke(
        this.userInvite.inviteId,
        this.selectedReason,
        this.selectedReason === RevocationReason.Otro ? this.reasonText : undefined
      );
      this.userInvite.currentStatus = UserStatus.Revoked;
      this.showRevokeForm = false;
      this.activeModal.close(DismissReason.Saved);
    } catch (error) {
      this.showSpinner = false;
    } finally {
      this.showSpinner = false;
    }
  }

  async confirmReactivate() {
    try {
      this.showSpinner = true;
      await this.credentialRevocationService.reactivate(this.userInvite.inviteId);
      this.userInvite.currentStatus = UserStatus.Registered;
      this.activeModal.close(DismissReason.Saved);
    } catch (error) {
      this.showSpinner = false;
    } finally {
      this.showSpinner = false;
    }
  }

  async blockUnblockUser() {
    if (this.userInvite.blockUnblockSignatureInProgress) return;

    const modalInstance = this.modalService.open(DialogModalComponent)
      .componentInstance as IBasicModalComponentInstance;
    modalInstance.title = this.isBlocked
      ? this.translate.instant('usersModule.unBlockUserTitle')
      : this.translate.instant('usersModule.blockUserTitle');
    modalInstance.body = this.isBlocked
      ? this.translate.instant('usersModule.unBlockUserBody', { UserFullName: this.userInvite.fullName })
      : this.translate.instant('usersModule.blockUserBody', { UserFullName: this.userInvite.fullName });
    modalInstance.type = BasicModalOperationResult.Warning;

    modalInstance.buttons = [
      {
        text: this.translate.instant('cancel'),
        cssClass: 'btn btn-danger',
        closeModalOnClick: true,
      },
      {
        text: this.translate.instant('confirm'),
        cssClass: 'btn btn-success',
        closeModalOnClick: true,
        handler: async () => {
          try {
            this.showSpinner = true;
            const wasBlocked = this.isBlocked;
            const response = await this.blockUnblockService.amiSignProcessBlockUnblockInvite(this.userInvite);
            if (response == OutgoingChatMessageStatusEnum.CONFIRMED) {
              const blockUnblockResponse = await this.blockUnblockService.blockUnblockInvite(
                this.userInvite.inviteId,
                Number(this.blockUnblockType.User),
                !this.userInvite.isBlocked
              );
              await this.blockUnblockService.updateSignProcessBlockUnblockInvite(blockUnblockResponse.inviteId);
              this.showSpinner = false;
              await this.showSuccessfulBlockUnBlockMessage(wasBlocked);
            }
            this.activeModal.close(DismissReason.Saved);
          } catch (error) {
            alert(this.translate.instant('signatureErrorMessage'));
            this.showSpinner = false;
          }
        },
      },
    ];
  }

  private async showSuccessfulBlockUnBlockMessage(wasBlocked: boolean) {
    const modalRef = this.modalService.open(DialogModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
    const modalInstance = modalRef.componentInstance as IBasicModalComponentInstance;
    modalInstance.body = wasBlocked
      ? this.translate.instant('usersModule.unBlockUserMessage', { UserFullName: this.userInvite.fullName })
      : this.translate.instant('usersModule.blockUserMessage', { UserFullName: this.userInvite.fullName });
    modalInstance.title = this.translate.instant('userUpdated');
    modalInstance.type = BasicModalOperationResult.Success;
    await modalRef.result;
  }

  closeModal() {
    this.activeModal.close(DismissReason.Cancelled);
  }
}
